
/**
 * Staff Selector Component
 * Displays current staff and allows selection from staff list
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography, CircularProgress, Divider, Button, TextField, Box, Chip, Snackbar, Alert } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { apiFetchJson } from '../api/dispatchClient';
import { mergeStaffPriorities, onStaffPriorityChange } from '../api/dispatchService';
import { isOnDuty, isFixedStaff } from '../utils/staffPriority';
import { isSharedAccount } from '../config/backend';
import StaffDutyDialog from './StaffDutyDialog';
import CurrentOperatorModal from './CurrentOperatorModal';
import { useIdleDetector } from '../hooks/useIdleDetector';
import { useAuth } from './AuthContext';

interface StaffData {
  id: number | null;
  name: string;
  login_id: string;
}

// モーダル解除を他タブに伝播するチャンネル
const OPERATOR_MODAL_CHANNEL = 'operator-modal-sync';

interface StaffSelectorProps {
  color?: any;
  size?: 'small' | 'medium' | 'large';
  user?: any;
  [key: string]: any;
}

/**
 * Staff Selector Icon with Menu
 */
export function StaffSelector({ color = 'inherit', size = 'medium', user, ...rest }: StaffSelectorProps): React.ReactElement {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentStaff, setCurrentStaff] = useState<StaffData | null>(null);
    const [tempName, setTempName] = useState<string>('');
    const [dutyDialogOpen, setDutyDialogOpen] = useState(false);
    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const [continuedToast, setContinuedToast] = useState<{ open: boolean; name: string }>({ open: false, name: '' });
    const open = Boolean(anchorEl);
    // 共用アカウント(那覇='staff' / 宮古=未設定)。店舗ごとに違うので instances.ts が正
    const isStaffAccount = isSharedAccount(user?.username);
    const { softLoggedOut } = useAuth() as { softLoggedOut: boolean };

    // softLoggedOut 時は全モーダルを閉じてログインフォームへの入力を解放
    useEffect(() => {
        if (softLoggedOut) {
            setOperatorModalOpen(false);
            setDutyDialogOpen(false);
            setAnchorEl(null);
        }
    }, [softLoggedOut]);

    // モーダル解除を他タブに伝播するための BroadcastChannel
    const operatorChannelRef = useRef<BroadcastChannel | null>(null);

    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return;
        const channel = new BroadcastChannel(OPERATOR_MODAL_CHANNEL);
        operatorChannelRef.current = channel;

        const handler = (e: MessageEvent) => {
            const msg = e.data;
            if (msg?.type === 'operator-selected' && msg.staff) {
                // 他タブでスタッフ選択 → 自タブも同期（bo_staff 更新 + モーダル閉じる）
                const staffData: StaffData = msg.staff;
                setCurrentStaff(staffData);
                try { localStorage.setItem('bo_staff', JSON.stringify(staffData)); } catch { /* ignore */ }
                (window as any).bo_staff = staffData;
                setOperatorModalOpen(false);
            } else if (msg?.type === 'operator-dismissed') {
                // 他タブでキャンセル → モーダルだけ閉じる（トーストは出さない）
                setOperatorModalOpen(false);
            }
        };

        channel.addEventListener('message', handler);
        return () => {
            channel.removeEventListener('message', handler);
            channel.close();
            operatorChannelRef.current = null;
        };
    }, []);

    // Load current staff from localStorage on mount
    useEffect(() => {
        const savedStaff = localStorage.getItem('bo_staff');
        if (savedStaff) {
            try {
                setCurrentStaff(JSON.parse(savedStaff));
            } catch (e) {
                console.error('Failed to parse saved staff:', e);
            }
        }
    }, []);

    // Sync user to staff if not 'staff' account
    useEffect(() => {
        if (user && !isSharedAccount(user.username)) {
            const autoStaff: StaffData = {
                id: user.id,
                name: user.name,
                login_id: user.username
            };

            // Update if different to maintain single source of truth
            if (!currentStaff || currentStaff.id !== autoStaff.id || currentStaff.name !== autoStaff.name) {
                setCurrentStaff(autoStaff);
                localStorage.setItem('bo_staff', JSON.stringify(autoStaff));
                (window as any).bo_staff = autoStaff;
            }
        }
    }, [user, currentStaff]);

    const fetchStaff = useCallback(async () => {
        setLoading(true);
        try {
            // NOTE: バックエンドの ?active=1 フィルタは ID 1-4 を除外してしまうため使わない。
            // per_page=100 で全件取得し、フロント側で is_active / is_deleted のみチェック。
            const response = await apiFetchJson('staff?per_page=100');
            if (response && response.data) {
                const activeStaff = response.data.filter(
                    (staff: any) => !staff.is_deleted && staff.is_active !== 0
                );
                // localStorageのpriority情報をマージ
                const merged = mergeStaffPriorities(activeStaff);
                // priority降順ソート（出勤者が上、オフが下）
                merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
                setStaffList(merged);
            }
        } catch (error) {
            console.error('Failed to fetch staff list:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTempName(currentStaff?.name || '');
        // 毎回取得して最新のpriority反映
        await fetchStaff();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTempNameSubmit = () => {
        if (!tempName.trim()) return;
        const staffData: StaffData = {
            id: null,
            name: tempName,
            login_id: 'temp'
        };
        setCurrentStaff(staffData);
        localStorage.setItem('bo_staff', JSON.stringify(staffData));
        (window as any).bo_staff = staffData;
        handleClose();
    };

    const handleStaffSelect = (staff: any) => {
        const staffData: StaffData = {
            id: staff.id,
            name: staff.staff_name,
            login_id: staff.staff_login_id
        };

        setCurrentStaff(staffData);
        localStorage.setItem('bo_staff', JSON.stringify(staffData));

        // Set as global variable
        (window as any).bo_staff = staffData;

        handleClose();
    };

    const handleClearStaff = () => {
        setCurrentStaff(null);
        localStorage.removeItem('bo_staff');
        (window as any).bo_staff = null;
        handleClose();
    };

    const handleOpenDutyDialog = () => {
        handleClose(); // メニューを閉じる
        setDutyDialogOpen(true);
    };

    const handleDutySaved = useCallback(() => {
        // 保存後にスタッフリストを即座に再マージ
        setStaffList(prev => {
            if (prev.length === 0) return prev;
            const merged = mergeStaffPriorities(prev);
            merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
            return merged;
        });
    }, []);

    // 優先度変更リスナー（他タブ or 同一タブからの更新を即座に反映）
    useEffect(() => {
        const cleanup = onStaffPriorityChange(() => {
            setStaffList(prev => {
                if (prev.length === 0) return prev;
                const merged = mergeStaffPriorities(prev);
                merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
                return merged;
            });
        });
        return cleanup;
    }, []);

    // staff アカウント時は、一時対応者モーダル用に初回フェッチしておく
    useEffect(() => {
        if (isStaffAccount && staffList.length === 0) {
            fetchStaff();
        }
    }, [isStaffAccount, staffList.length, fetchStaff]);

    // 「対応者？」モーダルで実際に選べる人がいるか（CurrentOperatorModal と同じ条件）
    const hasSelectableOperator = staffList.some(s => isOnDuty(s.priority) && !isFixedStaff(s));

    // 10秒無操作でモーダル自動表示（staff アカウントのみ）
    // 他のモーダル/ダイアログが開いている時は表示しない（チャットモーダル・ログインモーダル等の入力を邪魔しないため）
    useIdleDetector(
        10_000,
        () => {
            // 他のMUI Dialog が開いていたらスキップ（次のアイドル検知時に再評価）
            const otherDialogs = document.querySelectorAll('.MuiDialog-root');
            if (otherDialogs.length > 0) return;
            // 入力中のテキスト要素にフォーカスがあるならスキップ
            const active = document.activeElement;
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || (active as HTMLElement).isContentEditable)) {
                return;
            }
            setOperatorModalOpen(true);
        },
        // ⚠️ 選べる出勤者が1人もいないときは自動表示しない。
        //   出したところで「本日勤務のスタッフがいません」しか出ず、10秒ごとに
        //   出続けるだけになる(新店舗の初回ログイン時がまさにこの状態)。
        isStaffAccount && !operatorModalOpen && hasSelectableOperator,
    );

    const displayText = currentStaff
        ? `対応中:${currentStaff.name}`
        : '対応スタッフ';

    // 出勤スタッフとオフスタッフを分離
    const onDutyStaff = staffList.filter(s => isOnDuty(s.priority));
    const offDutyStaff = staffList.filter(s => !isOnDuty(s.priority));

    return (
        <>
            <Button
                color={color}
                // ⚠️ 共用アカウントかどうかで**開閉自体を分岐しない**こと。
                //   2026-08-03 まで `username === 'staff'`(那覇にしか無い login_id)で
                //   分岐しており、宮古では担当者ボタンが不活性になってメニュー内の
                //   「本日勤務」も開けず、出勤者を設定できないままだった。
                //   個人アカウントでの出し分けはメニューの中身側で行う。
                onClick={handleClick}
                aria-label="対応スタッフを選択"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textTransform: 'none',
                    lineHeight: 1.2,
                    minWidth: 'auto',
                    py: 0.5,
                    px: 1
                }}
            >
                <PersonIcon />
                <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2, whiteSpace: 'nowrap' }}>
                    {displayText}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        maxHeight: 500,
                        width: '280px',
                    },
                }}
            >
                {currentStaff && (
                    <>
                        <MenuItem disabled>
                            <Typography variant="caption" color="text.secondary">
                                現在の対応スタッフ
                            </Typography>
                        </MenuItem>
                        <MenuItem>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {currentStaff.name}
                            </Typography>
                        </MenuItem>
                        {isStaffAccount && (
                            <MenuItem onClick={handleClearStaff}>
                                <Typography variant="body2" color="error">
                                    クリア
                                </Typography>
                            </MenuItem>
                        )}
                        <Divider />
                    </>
                )}

                {/* 本日勤務ボタン */}
                <Box sx={{ px: 2, py: 0.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<CalendarTodayIcon />}
                        onClick={handleOpenDutyDialog}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                        本日勤務
                    </Button>
                </Box>
                <Divider sx={{ my: 0.5 }} />

                {/* スタッフ選択は共用アカウントのみ。個人ログインでは対応者＝ログイン者で
                    固定され(上の自動同期)、選ばせても無言で戻るため出さない。
                    「本日勤務」は上に出ているので、どのアカウントでも出勤者は設定できる */}
                {isStaffAccount && <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">
                        スタッフ選択
                    </Typography>
                </MenuItem>}

                {isStaffAccount && (loading ? (
                    <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2">読み込み中...</Typography>
                    </MenuItem>
                ) : (
                    <>
                        {/* 出勤スタッフ */}
                        {onDutyStaff.length > 0 && (
                            <>
                                {onDutyStaff.map((staff) => (
                                    <MenuItem
                                        key={staff.id}
                                        onClick={() => handleStaffSelect(staff)}
                                        selected={currentStaff?.id === staff.id}
                                    >
                                        <Chip
                                            label="出勤"
                                            size="small"
                                            color="success"
                                            sx={{ mr: 1, height: 20, fontSize: '0.6rem' }}
                                        />
                                        <Typography variant="body2">
                                            {staff.staff_name}
                                        </Typography>
                                    </MenuItem>
                                ))}
                                {offDutyStaff.length > 0 && <Divider sx={{ my: 0.5 }} />}
                            </>
                        )}
                        {/* オフスタッフ */}
                        {offDutyStaff.map((staff) => (
                            <MenuItem
                                key={staff.id}
                                onClick={() => handleStaffSelect(staff)}
                                selected={currentStaff?.id === staff.id}
                            >
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    {staff.staff_name}
                                </Typography>
                            </MenuItem>
                        ))}
                        {staffList.length === 0 && !loading && (
                            <MenuItem disabled>
                                <Typography variant="body2" color="text.secondary">
                                    スタッフが見つかりません
                                </Typography>
                            </MenuItem>
                        )}
                    </>
                ))}

                {isStaffAccount && (
                    <>
                        <Divider />
                        <Box sx={{ p: 1, px: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                一時対応者
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="名前を入力"
                                value={tempName}
                                onChange={(e) => setTempName(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') handleTempNameSubmit();
                                }}
                                onClick={(e) => e.stopPropagation()}
                                variant="outlined"
                            />
                        </Box>
                    </>
                )}
            </Menu>

            {/* 本日勤務モーダル */}
            <StaffDutyDialog
                open={dutyDialogOpen}
                onClose={() => setDutyDialogOpen(false)}
                staffList={staffList}
                onSaved={handleDutySaved}
            />

            {/* 一時対応者 自動プロンプト（10秒無操作時） */}
            <CurrentOperatorModal
                open={operatorModalOpen}
                staffList={staffList}
                onSelect={(staff) => {
                    handleStaffSelect(staff);
                    setOperatorModalOpen(false);
                    // 他タブのモーダルも同じスタッフで閉じる
                    try {
                        operatorChannelRef.current?.postMessage({
                            type: 'operator-selected',
                            staff: {
                                id: staff.id,
                                name: staff.staff_name,
                                login_id: staff.staff_login_id,
                            },
                        });
                    } catch { /* ignore */ }
                }}
                onCancel={() => {
                    setOperatorModalOpen(false);
                    setContinuedToast({
                        open: true,
                        name: currentStaff?.name || '未設定',
                    });
                    // 他タブのモーダルも閉じる（トーストは出さない）
                    try {
                        operatorChannelRef.current?.postMessage({ type: 'operator-dismissed' });
                    } catch { /* ignore */ }
                }}
            />

            {/* 継続トースト（キャンセル時） */}
            <Snackbar
                open={continuedToast.open}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={3000}
                onClose={() => setContinuedToast(prev => ({ ...prev, open: false }))}
            >
                <Alert severity="info" variant="filled" sx={{ fontWeight: 600 }}>
                    対応者: {continuedToast.name}（継続）
                </Alert>
            </Snackbar>
        </>
    );
}

export default StaffSelector;
